/**
 * The computed budget, read out with its own uncertainty stated.
 *
 * This markup lived inside `FirstRun`, which was the only screen that computed
 * a budget rather than asking for one. It now has two homes — first run, and
 * the budget card on **You**, where the alternative was hand arithmetic — and
 * two copies of a readout drift. They would not drift about the big number;
 * they would drift about the *caveats*, which is worse, because a caveat that
 * appears on one screen and not the other reads as a different promise rather
 * than as an omission.
 *
 * So the figure, the rate, the macro split and both of the awkward cases live
 * here, and a host supplies only what is genuinely its own: the label, the
 * footnote about where this is edited afterwards, and the action that does
 * something with the number.
 *
 * ## The two awkward cases
 *
 * **A rate of zero.** `impliedRate` answers zero for maintenance, and
 * "+0.00 kg a week" is a sentence that looks like a rounding error rather than
 * like a plan. It is a plan — holding steady is what someone with no target
 * weight is doing — so it is said in words.
 *
 * **The 1,200 kcal floor.** `estimateBudget` refuses to go below it, because a
 * deficit steeper than the body can supply from fat comes out of muscle and
 * adherence instead. When the floor bites, the rate shown above is the rate
 * that was *asked for* and not the one the budget delivers, and saying nothing
 * would leave the screen quietly promising a loss it has just declined to aim
 * for.
 */

import { kcalPerDayFor, type BudgetEstimate } from "@calorio/core";
import type { ReactNode } from "react";

interface EstimatedBudgetProps {
  estimate: BudgetEstimate;
  /** Overrides the default heading. */
  label?: string;
  /** The host's own note under the figures — chiefly, where this is edited. */
  footnote?: string;
  /** Sunken rather than outlined, for a readout that sits inside a `.card`. */
  inset?: boolean;
  /** The host's action on the number: start tracking, fill the fields, … */
  children?: ReactNode;
}

export function EstimatedBudget({
  estimate,
  label = "Your daily budget",
  footnote,
  inset = false,
  children,
}: EstimatedBudgetProps) {
  const rate = estimate.rateKgPerWeek;
  // The same threshold the weight-goal card uses for "is there a rate here at
  // all", so the two screens cannot disagree about where maintenance begins.
  const steady = Math.abs(rate) < 0.005;

  // What the rate asked for, before the floor had its say. `budgetKcal` is
  // `max(1200, wanted)`, so a wanted figure below the budget is the floor
  // having lifted it — and the delivered rate is whatever the gap from
  // maintenance actually buys, read back through the same conversion.
  const wanted = Math.round(estimate.maintenanceKcal + kcalPerDayFor(rate));
  const floored = wanted < estimate.budgetKcal;
  const delivered = (estimate.budgetKcal - estimate.maintenanceKcal) / kcalPerDayFor(1);

  return (
    <div className={inset ? "budget budget--inset" : "budget"}>
      <span className="budget__label">{label}</span>
      <div>
        <span className="num budget__figure">{estimate.budgetKcal.toLocaleString()}</span>
        <span className="budget__unit">
          {steady ? (
            "kcal · holding steady"
          ) : (
            <>
              kcal ·{" "}
              <span className="num">
                {rate > 0 ? "+" : ""}
                {rate.toFixed(2)}
              </span>{" "}
              kg a week
            </>
          )}
        </span>
      </div>
      <div className="budget__macros num">
        <span>
          <span className="budget__p">P</span> {estimate.protein_g} g
        </span>
        <span>
          <span className="budget__c">C</span> {estimate.carbs_g} g
        </span>
        <span>
          <span className="budget__f">F</span> {estimate.fat_g} g
        </span>
      </div>

      {floored && (
        <p className="manual__note">
          That is the <span className="num">1,200</span> kcal floor rather than the rate above —
          eating to it comes to about <span className="num">{delivered.toFixed(2)}</span> kg a week.
          Anything steeper comes out of muscle, not fat.
        </p>
      )}

      {footnote && <p className="manual__note">{footnote}</p>}

      {children}
    </div>
  );
}
